import { useState, useEffect } from 'react';
import { collection, onSnapshot, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconPlus } from '@tabler/icons-react';
import { inputStyle, labelStyle } from '../contracts/contractsApi';
import { call, KINDS, KIND_LABEL, Chip, fmt, openBase64Pdf, banner } from './packsApi';

const FIELD_HELP = 'Standard fields: {{agriware.legalName}} {{agriware.companyNumber}} {{agriware.registry}} {{agriware.registeredAddress}} {{agriware.signatoryName}} {{agriware.signatoryRole}} {{party.legalName}} {{party.tradingName}} {{party.companyNumber}} {{party.registry}} {{party.registeredAddress}} {{signer.name}} {{signer.role}} {{signer.email}} {{project.name}} {{date}}. Anything set per pack: {{pack.custom.yourKey}}.';

export default function Templates() {
  const [templates, setTemplates] = useState(null);
  const [open, setOpen] = useState(null); // template id, or 'new'
  const [msg, setMsg] = useState(null);
  useEffect(() => onSnapshot(collection(db, 'contractTemplates'), s =>
    setTemplates(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.status === b.status ? a.name.localeCompare(b.name) : a.status === 'active' ? -1 : 1))),
  e => setMsg({ tone: 'danger', text: e.message })), []);

  return (
    <Layout>
      <PageHeader title="Contract templates" subtitle="Versioned HTML templates with merge fields. Packs pin the version they use."
        actions={<Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setOpen('new')}>New template</Button>} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1100 }}>
        {msg && <div style={banner(msg.tone)}>{msg.text}</div>}
        {open === 'new' && <TemplateEditor onDone={(text) => { setOpen(null); if (text) setMsg({ tone: 'success', text }); }} />}
        {templates && templates.length === 0 && open !== 'new' && <Card><div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>No templates yet.</div></Card>}
        {templates?.map(t => (
          <Card key={t.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => setOpen(open === t.id ? null : t.id)}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{t.name}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{KIND_LABEL[t.kind]} · version {t.currentVersion} · updated {fmt(t.updatedAt)}</div>
              </div>
              <Chip label={t.status === 'active' ? 'Active' : 'Archived'} tone={t.status === 'active' ? 'good' : 'none'} />
            </div>
            {open === t.id && <TemplateDetail t={t} onMsg={setMsg} />}
          </Card>
        ))}
      </div>
    </Layout>
  );
}

function TemplateDetail({ t, onMsg }) {
  const [versions, setVersions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(null);
  const load = () => getDocs(query(collection(db, 'contractTemplates', t.id, 'versions'), orderBy('version', 'desc'))).then(s => setVersions(s.docs.map(d => d.data())));
  useEffect(() => { load(); }, [t.currentVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = async (version) => {
    setBusy(`p${version}`);
    try { openBase64Pdf((await call('previewTemplate', { templateId: t.id, version })).pdfBase64); }
    catch (e) { onMsg({ tone: 'danger', text: e.message }); }
    setBusy(null);
  };
  const setStatus = async (status) => {
    setBusy('s');
    try { await call('setTemplateStatus', { templateId: t.id, status }); } catch (e) { onMsg({ tone: 'danger', text: e.message }); }
    setBusy(null);
  };

  return (
    <div style={{ marginTop: 14, borderTop: '0.5px solid var(--color-border-light)', paddingTop: 12 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <Button size="sm" onClick={() => setEditing(e => !e)}>{editing ? 'Cancel new version' : 'Upload new version'}</Button>
        <Button size="sm" variant="secondary" loading={busy === `p${t.currentVersion}`} onClick={() => preview(t.currentVersion)}>Preview as PDF</Button>
        <Button size="sm" variant="ghost" loading={busy === 's'} onClick={() => setStatus(t.status === 'active' ? 'archived' : 'active')}>{t.status === 'active' ? 'Archive' : 'Restore'}</Button>
      </div>
      {editing && <TemplateEditor existing={t} onDone={(text) => { setEditing(false); if (text) onMsg({ tone: 'success', text }); load(); }} />}
      {versions?.map(v => (
        <div key={v.version} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 150px 90px', gap: 10, padding: '8px 0', borderTop: '0.5px solid var(--color-border-light)', fontSize: 12, alignItems: 'start' }}>
          <div style={{ fontWeight: 500 }}>v{v.version}{v.version === t.currentVersion ? ' (current)' : ''}</div>
          <div>
            {v.notes && <div style={{ marginBottom: 4 }}>{v.notes}</div>}
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--color-text-tertiary)', lineHeight: 1.6 }}>{(v.mergeFields || []).map(f => `{{${f}}}`).join(' ') || 'No merge fields'}</div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{fmt(v.createdAt)}</div>
          <Button size="sm" variant="ghost" loading={busy === `p${v.version}`} onClick={() => preview(v.version)}>Preview</Button>
        </div>
      ))}
    </div>
  );
}

function TemplateEditor({ existing, onDone }) {
  const [name, setName] = useState(existing?.name || '');
  const [kind, setKind] = useState(existing?.kind || 'agreement');
  const [html, setHtml] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fromFile = async (f) => { if (f) setHtml(await f.text()); };
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const r = await call('saveTemplate', { templateId: existing?.id, name: name.trim(), kind, html, notes });
      onDone(`Saved "${name.trim()}" version ${r.version} with ${r.mergeFields.length} merge fields.`);
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <div style={{ background: 'var(--color-bg-secondary)', padding: 14, borderRadius: 'var(--radius-md)', marginBottom: 12 }}>
      {error && <div style={{ ...banner('danger'), marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
        <div><label style={labelStyle}>Name</label><input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="Mutual Non-Disclosure Agreement" /></div>
        <div><label style={labelStyle}>Kind</label><select style={inputStyle} value={kind} onChange={e => setKind(e.target.value)}>{KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
      </div>
      <label style={labelStyle}>Template HTML (paste, or choose a file)</label>
      <input type="file" accept=".html,.htm,text/html" style={{ fontSize: 11, marginBottom: 6 }} onChange={e => fromFile(e.target.files[0])} />
      <textarea rows={12} style={{ ...inputStyle, height: 'auto', padding: 8, fontFamily: 'var(--font-mono)', fontSize: 11 }} value={html} onChange={e => setHtml(e.target.value)} />
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', margin: '6px 0 10px', lineHeight: 1.5 }}>{FIELD_HELP}</div>
      {existing && <><label style={labelStyle}>What changed in this version</label><input style={{ ...inputStyle, marginBottom: 10 }} value={notes} onChange={e => setNotes(e.target.value)} /></>}
      <Button loading={busy} disabled={!name.trim() || !html.trim()} onClick={submit}>{existing ? `Save as version ${existing.currentVersion + 1}` : 'Save template'}</Button>
      {!existing && <span style={{ marginLeft: 8 }}><Button variant="ghost" onClick={() => onDone(null)}>Cancel</Button></span>}
    </div>
  );
}
